<template>
    <div v-if="loading" class="flex flex-col gap-8">
        <section
            v-for="n in skeletonSectionCount"
            :key="`skeleton-${n}`"
            class="flex flex-col gap-6"
        >
            <div class="flex flex-col gap-2">
                <skeleton height="2rem" width="14rem" />
                <skeleton height="1rem" width="22rem" />
            </div>
            <div class="grid grid-cols-2 gap-2 md:gap-3 lg:grid-cols-4 mt-4">
                <div v-for="i in 4" :key="i" class="flex flex-col gap-2">
                    <skeleton height="12rem" class="!rounded-none" />
                    <skeleton height="1rem" width="80%" />
                    <skeleton height="0.75rem" width="40%" />
                    <skeleton height="1.5rem" width="60%" />
                </div>
            </div>
        </section>
    </div>

    <div v-else class="flex flex-col gap-8">
        <section
            v-for="(section, sIdx) in sortedSections"
            :key="section.id"
            class="flex flex-col gap-6"
        >
            <div class="flex flex-col items-center text-center gap-1 !mb-10">
                <h3 class="text-2xl md:text-3xl !font-bold !mb-10">
                    {{ sectionTitle(section) }}
                </h3>
                <p
                    v-if="section.overview"
                    class="text-sm md:text-base text-gray-600 m-0 max-w-3xl mx-auto"
                >
                    {{ section.overview }}
                </p>
            </div>
            <template v-if="section.tours?.length">
                <tours
                    v-if="section.type === 'tag'"
                    :tours="visibleTours(section)"
                    layout="grid"
                    fixedlayout
                    hide-cta
                    :priority="sIdx === 0"
                    :country="location?.country_slug"
                    :region="location?.region_slug"
                    :loading="loading"
                />
                <div v-else class="mt-2">
                    <tours
                        :tours="section.tours"
                        layout="scroll"
                        fixedlayout
                        hide-cta
                        :country="location?.country_slug"
                        :region="location?.region_slug"
                        :loading="loading"
                    />
                </div>

                <div
                    v-if="
                        section.type === 'tag' && section.tours.length > limit
                    "
                    class="flex justify-center pt-2"
                >
                    <div
                        @click="
                            onSearch({
                                tour_category: [slugify(section.tag_name)],
                            })
                        "
                        class="px-6 py-2 rounded-full border border-gray-400 text-sm font-medium hover:bg-gray-100 transition-colors cursor-pointer"
                    >
                        See all {{ section.tours.length }} experiences
                        </div>
                </div>
            </template>
            <div v-else class="text-sm text-gray-500 italic">
                No tours available in this section yet.
            </div>
        </section>

        <div
            v-if="!sortedSections.length"
            class="text-center py-10 text-gray-500"
        >
            No explore sections have been configured for this destination.
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { Location, Tour } from "@/types";
import { Skeleton } from "primevue";
import Tours from "@/Components/Tour/Tours.vue";

interface ExploreSection {
    id: number;
    type: string;
    title?: string | null;
    overview?: string | null;
    tag_id?: number | null;
    tag_name?: string | null;
    tours?: Tour[];
    position?: number;
    published_at?: string | null;
}

const props = defineProps<{
    location: Location;
    sections?: ExploreSection[];
    loading?: boolean;
}>();

const emit = defineEmits(["search"]);

const limit = 8;
const skeletonSectionCount = 2;

const sortedSections = computed(() =>
    [...(props.sections ?? [])].sort(
        (a, b) => (a.position ?? 0) - (b.position ?? 0),
    ),
);

const onSearch = function (data: Record<string, any>) {
    emit("search", data);
    window.scrollTo({ top: 0, behavior: "smooth" });
};

const visibleTours = (section: ExploreSection) =>
    (section.tours ?? []).slice(0, limit);

const sectionTitle = (section: ExploreSection) =>
    section.title || section.tag_name || "Tours";

const slugify = (value: string) =>
    value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
</script>
